import { Avatar } from "@ui/Avatar";
import { Button } from "@ui/Button";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { HStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import { type Accessor, createSignal, onCleanup, Show } from "solid-js";
import { formatCountry } from "../askPageAccess";
import { askPageVars } from "../pageVars";
import { JoinButton } from "./JoinButton";
import { ShareCreatorButton } from "./ShareCreatorButton";
import "../AskPageApp.css";

type Props = {
	onJoin?: () => void;
	joinBusy?: Accessor<boolean>;
	joinError?: Accessor<string | null>;
};

export const CreatorIdentity = (props: Props) => {
	const vars = askPageVars();
	const country = () => formatCountry(vars.influencer_country);

	const [bioExpanded, setBioExpanded] = createSignal(false);
	const [bioClamped, setBioClamped] = createSignal(false);

	// The bio is only clamped in the narrow layout, which is a container query,
	// so whether it overflows has to be measured rather than derived.
	const observeBio = (el: HTMLElement) => {
		const observer = new ResizeObserver(() => {
			if (!bioExpanded()) {
				setBioClamped(el.scrollHeight > el.clientHeight + 1);
			}
		});
		observer.observe(el);
		onCleanup(() => observer.disconnect());
	};

	return (
		<>
			<div class="askgvt1-banner">
				<Show when={vars.banner_web_url ?? vars.banner_url}>
					{(url) => (
						<img
							src={url()}
							alt=""
							width={1000}
							height={200}
							decoding="async"
							fetchpriority="high"
						/>
					)}
				</Show>
			</div>
			<div class="askgvt1-identity">
				<span class="askgvt1-avatar">
					<Avatar
						alt={vars.influencer_display_name}
						src={vars.influencer_avatar_url ?? undefined}
						size="large"
					/>
				</span>
				<HStack class="askgvt1-identity__name" align="center" spacing="small">
					<Text as="h1" size="medium-large" weight="semi-bold" color="ink">
						{vars.influencer_display_name}
					</Text>
					<MaterialSymbol symbol="verified" filled color="primary" />
				</HStack>
				<HStack
					class="askgvt1-identity__actions"
					spacing="medium"
					align="center"
				>
					<JoinButton onJoin={props.onJoin} joinBusy={props.joinBusy} />
					<ShareCreatorButton />
				</HStack>
				<div class="askgvt1-identity__meta">
					<Text
						class="askgvt1-identity__handle"
						family="mono"
						size="small"
						color="fg3"
					>
						@{vars.influencer_handle}
					</Text>
					<Show when={country()}>
						<Text class="askgvt1-identity__country" size="small" color="fg3">
							{country()}
						</Text>
					</Show>
				</div>
				<div class="askgvt1-identity__details">
					<Show when={props.joinError?.()}>
						<Text as="p" size="small" color="error">
							{props.joinError?.()}
						</Text>
					</Show>
					<Show when={vars.influencer_bio}>
						<div class="askgvt1-identity__bio">
							<p
								ref={observeBio}
								classList={{
									"askgvt1-identity__bio-text": true,
									"askgvt1-identity__bio-text--expanded": bioExpanded(),
								}}
							>
								<Text as="span" size="intermediate" color="fg2">
									{vars.influencer_bio}
								</Text>
							</p>
							<Show when={bioClamped() || bioExpanded()}>
								<Button
									class="askgvt1-identity__bio-toggle"
									variant="text"
									theme="monochrome"
									shape="square"
									size="small"
									symbol={bioExpanded() ? "expand_less" : "expand_more"}
									aria-label={bioExpanded() ? "Show less" : "Show more"}
									aria-expanded={bioExpanded()}
									onClick={() => setBioExpanded(!bioExpanded())}
								/>
							</Show>
						</div>
					</Show>
				</div>
			</div>
		</>
	);
};
